/**
 * Advanced mode (spec §21 progressive disclosure): the full input surface.
 * Four tabs — Household, Accounts (all 15 types), Income streams, Events.
 * Each item is a collapsible card so the surface stays calm.
 */
import { useState } from 'react';
import type { AccountType, IncomeType, OneTimeEvent, Plan } from '../model/types';
import { ACCOUNT_META, ACCOUNT_TYPE_LABELS, DEFAULT_ALLOCATION } from '../model/accountMeta';
import { contributionLimits } from '../engine/data/contributionLimits';
import { CurrencyField, NumberField } from './Inputs';
import { BudgetTab } from './BudgetTab';
import { PropertyTab, DebtTab } from './PropertyDebtTabs';
import { HealthcareTab } from './HealthcareTab';
import { StrategyTab } from './StrategyTab';
import { ProfilesTab } from './ProfilesTab';
import { SaleImpactNote } from './SaleImpactNote';
import { TIPS } from './tips';
import { fmtCompactUsd } from './format';

interface Props {
  plan: Plan;
  onChange: (updated: Plan) => void;
}

type Tab = 'household' | 'accounts' | 'income' | 'budget' | 'health' | 'property' | 'debts' | 'events' | 'strategy' | 'profiles';

const INCOME_TYPE_LABELS: Record<IncomeType, string> = {
  w2: 'Salary (W-2)',
  self_employment: 'Self-employment',
  pension_monthly: 'Pension (monthly)',
  pension_lump_sum: 'Pension (lump sum)',
  pension_cash_balance: 'Pension (cash balance)',
  annuity_immediate: 'Annuity (immediate)',
  annuity_deferred: 'Annuity (deferred)',
  rental: 'Rental income',
  royalty: 'Royalties',
  other_passive: 'Other passive',
};

/** Set an account's stock share; bonds+cash absorb the rest, keeping their
 *  existing ratio (so an all-cash money-market account stays all cash). */
function setEquityShare(acct: Plan['accounts'][number], equityPct: number) {
  const prev = acct.allocation ?? { equityPct: 60, bondPct: 35, cashPct: 5 };
  const rest = Math.max(0, 100 - equityPct);
  const nonStock = prev.bondPct + prev.cashPct;
  const cashShare = nonStock > 0 ? prev.cashPct / nonStock : 5 / 40;
  acct.allocation = {
    equityPct,
    bondPct: Math.round(rest * (1 - cashShare)),
    cashPct: rest - Math.round(rest * (1 - cashShare)),
  };
}

export function AdvancedInputs({ plan, onChange }: Props) {
  const [tab, setTab] = useState<Tab>('household');

  function update(mut: (p: Plan) => void) {
    const next = structuredClone(plan);
    mut(next);
    onChange(next);
  }

  const people = plan.household.people;

  return (
    <div>
      <div className="seg" role="tablist" aria-label="Advanced sections" style={{ marginBottom: 'var(--space-4)' }}>
        {(['household', 'accounts', 'income', 'budget', 'health', 'property', 'debts', 'events', 'strategy', 'profiles'] as Tab[]).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} aria-pressed={tab === t} onClick={() => setTab(t)}>
            {t === 'household' ? 'Household' : t === 'accounts' ? `Accounts (${plan.accounts.length})`
              : t === 'income' ? `Income (${plan.incomes.length})`
              : t === 'budget' ? 'Budget'
              : t === 'health' ? 'Healthcare'
              : t === 'property' ? `Property (${plan.properties.length})`
              : t === 'debts' ? `Debts (${plan.debts.length})`
              : t === 'events' ? `Events (${plan.oneTimeEvents.length})`
              : t === 'strategy' ? 'Strategy'
              : 'Assumptions'}
          </button>
        ))}
      </div>

      {tab === 'household' && <HouseholdTab plan={plan} update={update} />}
      {tab === 'accounts' && <AccountsTab plan={plan} update={update} />}
      {tab === 'income' && <IncomeTab plan={plan} update={update} />}
      {tab === 'budget' && <BudgetTab plan={plan} update={update} />}
      {tab === 'health' && <HealthcareTab plan={plan} update={update} />}
      {tab === 'property' && <PropertyTab plan={plan} update={update} />}
      {tab === 'debts' && <DebtTab plan={plan} update={update} />}
      {tab === 'events' && <EventsTab plan={plan} update={update} />}
      {tab === 'strategy' && <StrategyTab plan={plan} update={update} />}
      {tab === 'profiles' && <ProfilesTab plan={plan} update={update} />}

      {people.length === 2 && (
        <p className="hint" style={{ marginTop: 'var(--space-3)' }}>
          Spousal and survivor Social Security benefits are modeled (the lower earner receives
          the larger of their own or the spousal amount; a widowed spouse steps up to the
          deceased's benefit), and pensions honor survivor elections. See the claiming
          explorer below for the best ages to claim.
        </p>
      )}
    </div>
  );
}

// ── Household ───────────────────────────────────────────────────────────────

function HouseholdTab({ plan, update }: { plan: Plan; update: (mut: (p: Plan) => void) => void }) {
  const people = plan.household.people;
  return (
    <div>
      {people.map((person, i) => (
        <details key={person.id} className="item-card" open={people.length === 1}>
          <summary>
            <strong>{person.label}</strong>
            <span className="sub">born {person.birthYear} · retires at {person.retirementAge}</span>
          </summary>
          <div className="inputs-grid">
            <div className="field">
              <label>
                Label
                <input
                  value={person.label}
                  onChange={(e) => update((p) => { p.household.people[i]!.label = e.target.value; })}
                />
              </label>
              <span className="hint">A name is never required</span>
            </div>
            <NumberField label="Birth year" value={person.birthYear} min={1920} max={2010}
              onCommit={(v) => update((p) => { p.household.people[i]!.birthYear = v; })} />
            <NumberField label="Birth month" value={person.birthMonth} min={1} max={12}
              onCommit={(v) => update((p) => { p.household.people[i]!.birthMonth = v; })} />
            <NumberField label="Retirement age" value={person.retirementAge} min={35} max={80}
              onCommit={(v) => update((p) => { p.household.people[i]!.retirementAge = v; })} />
            <NumberField label="Plan to age" value={person.lifeExpectancyAge} min={70} max={105}
              onCommit={(v) => update((p) => { p.household.people[i]!.lifeExpectancyAge = v; })} />
            <NumberField label="SS claiming age" value={person.socialSecurity.claimingAge} min={62} max={70}
              onCommit={(v) => update((p) => { p.household.people[i]!.socialSecurity.claimingAge = v; })} />
            <CurrencyField label="SS benefit at FRA ($/mo)"
              value={person.socialSecurity.estimatedMonthlyAtFRA ?? 0}
              hint="From the SSA.gov statement"
              onCommit={(v) => update((p) => { p.household.people[i]!.socialSecurity.estimatedMonthlyAtFRA = v; })} />
          </div>
          {people.length === 2 && (
            <button
              className="btn danger"
              onClick={() => {
                const owns =
                  plan.accounts.some((a) => a.ownerId === person.id) ||
                  plan.incomes.some((s) => s.ownerId === person.id);
                if (owns) {
                  alert('Reassign or remove this person’s accounts and income streams first.');
                  return;
                }
                update((p) => { p.household.people.splice(i, 1); });
              }}
            >
              Remove {person.label}
            </button>
          )}
        </details>
      ))}

      {people.length === 1 && (
        <button
          className="btn"
          onClick={() =>
            update((p) => {
              p.household.people.push({
                id: `person_${p.household.people.length + 1}_${p.household.people[0]!.id}`,
                label: 'Spouse',
                birthYear: p.household.people[0]!.birthYear,
                birthMonth: 1,
                retirementAge: 65,
                lifeExpectancyAge: 92,
                socialSecurity: { claimingAge: 67 },
              });
              p.household.filingStatus = 'mfj';
            })
          }
        >
          + Add spouse / partner
        </button>
      )}

      <h3 style={{ margin: 'var(--space-6) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        Moving in retirement
      </h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Relocating changes the state tax picture from that year on — one of the highest-value
        questions this tool can answer.
      </p>
      {plan.household.relocations.map((rel, i) => (
        <div key={i} className="inputs-grid item-card" style={{ padding: 'var(--space-3) var(--space-4)' }}>
          <NumberField label="Move in year" value={rel.year} min={2026} max={2100}
            onCommit={(v) => update((p) => { p.household.relocations[i]!.year = v; })} />
          <div className="field">
            <label>
              To state
              <select value={rel.toState}
                onChange={(e) => update((p) => { p.household.relocations[i]!.toState = e.target.value; })}>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <div className="field" style={{ justifyContent: 'flex-end' }}>
            <button className="btn danger"
              onClick={() => update((p) => { p.household.relocations.splice(i, 1); })}>
              Remove move
            </button>
          </div>
        </div>
      ))}
      <div className="add-row">
        <button className="btn"
          onClick={() => update((p) => {
            p.household.relocations.push({ year: 2036, toState: 'FL' });
          })}>
          + Add a move
        </button>
      </div>

      <h3 style={{ margin: 'var(--space-6) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        State tax override
      </h3>
      <div className="inputs-grid">
        <NumberField label="Custom effective state rate % (0 = use the model)" min={0} max={15}
          value={Math.round((plan.household.customStateEffectiveRate ?? 0) * 100)}
          hint="Replaces all state tax modeling with a flat rate — never be blocked by a state we approximate"
          onCommit={(v) => update((p) => {
            p.household.customStateEffectiveRate = v === 0 ? undefined : v / 100;
          })} />
      </div>
    </div>
  );
}

const US_STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'];

// ── Accounts ────────────────────────────────────────────────────────────────

function isEmployerPlan(t: AccountType): boolean {
  return ['trad_401k', 'trad_403b', 'trad_457b', 'roth_401k', 'roth_403b', 'roth_457b'].includes(t);
}

function limitHint(type: AccountType, age: number): string | null {
  const L = contributionLimits(2026);
  const catchUp = age >= 60 && age <= 63 ? L.employer.superCatchUp60to63 : age >= 50 ? L.employer.catchUp50 : 0;
  if (isEmployerPlan(type)) {
    const total = L.employer.electiveDeferral + catchUp;
    const sep = type.includes('457') ? ' (457(b) has its own separate limit)' : '';
    return `2026 limit at age ${age}: $${total.toLocaleString()}${sep}`;
  }
  if (type === 'trad_ira' || type === 'roth_ira') {
    const total = L.ira.limit + (age >= 50 ? L.ira.catchUp50 : 0);
    return `2026 IRA limit at age ${age}: $${total.toLocaleString()}`;
  }
  if (type === 'hsa') {
    const total = L.hsa.selfOnly + (age >= 55 ? L.hsa.catchUp55 : 0);
    return `2026 HSA limit (self-only) at age ${age}: $${total.toLocaleString()}`;
  }
  return null;
}

function isStale(asOfDate?: string): boolean {
  if (!asOfDate) return false;
  const d = new Date(asOfDate);
  return Number.isFinite(d.getTime()) && Date.now() - d.getTime() > 365 * 24 * 3600 * 1000;
}

function AccountsTab({ plan, update }: { plan: Plan; update: (mut: (p: Plan) => void) => void }) {
  const people = plan.household.people;
  const [newType, setNewType] = useState<AccountType>('trad_401k');

  return (
    <div>
      {plan.accounts.map((acct, i) => {
        const owner = people.find((p) => p.id === acct.ownerId);
        const age = owner ? 2026 - owner.birthYear : 50;
        const meta = ACCOUNT_META[acct.type];
        const hint = limitHint(acct.type, age);
        return (
          <details key={acct.id} className="item-card">
            <summary>
              <strong>{acct.label || ACCOUNT_TYPE_LABELS[acct.type]}</strong>
              <span className="sub">
                {ACCOUNT_TYPE_LABELS[acct.type]} · {owner?.label ?? '?'} · {fmtCompactUsd(acct.balance)}
                {isStale(acct.asOfDate) && <span className="badge-stale"> balance over a year old</span>}
              </span>
            </summary>
            <div className="inputs-grid">
              <div className="field">
                <label>
                  Label
                  <input value={acct.label}
                    onChange={(e) => update((p) => { p.accounts[i]!.label = e.target.value; })} />
                </label>
              </div>
              <div className="field">
                <label>
                  Type
                  <select value={acct.type}
                    onChange={(e) => update((p) => { p.accounts[i]!.type = e.target.value as AccountType; })}>
                    {(Object.keys(ACCOUNT_TYPE_LABELS) as AccountType[]).map((t) => (
                      <option key={t} value={t}>{ACCOUNT_TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="field">
                <label>
                  Owner
                  <select value={acct.ownerId}
                    onChange={(e) => update((p) => { p.accounts[i]!.ownerId = e.target.value; })}>
                    {people.map((per) => <option key={per.id} value={per.id}>{per.label}</option>)}
                  </select>
                </label>
              </div>
              <CurrencyField label="Balance" value={acct.balance} tip={TIPS.balance}
                onCommit={(v) => update((p) => { p.accounts[i]!.balance = v; })} />
              <div className="field">
                <label>
                  Balance as of
                  <input type="date" value={acct.asOfDate ?? ''}
                    onChange={(e) => update((p) => { p.accounts[i]!.asOfDate = e.target.value || undefined; })} />
                </label>
                <span className="hint">So stale numbers are visible</span>
              </div>
              {meta.taxFamily === 'aftertax' && (
                <CurrencyField label="Cost basis" value={acct.costBasis ?? 0} tip={TIPS.costBasis}
                  hint="What you paid in — sets the taxable gain on withdrawals"
                  onCommit={(v) => update((p) => { p.accounts[i]!.costBasis = v; })} />
              )}
              {meta.taxFamily === 'roth' && (
                <CurrencyField label="Of that, your own contributions"
                  value={acct.rothContributionBasis ?? 0} tip={TIPS.rothContributionBasis}
                  hint="Money you put in yourself — not growth, and not conversions. You can withdraw it at any age, tax- and penalty-free. Left at 0 the plan assumes none of it is reachable early."
                  onCommit={(v) => update((p) => { p.accounts[i]!.rothContributionBasis = v || undefined; })} />
              )}
              {meta.rule55Possible && (
                <label className="field-check">
                  <input type="checkbox" checked={acct.currentEmployerPlan ?? false}
                    onChange={(e) => update((p) => {
                      p.accounts[i]!.currentEmployerPlan = e.target.checked || undefined;
                    })} />
                  <span>
                    <strong>This is the employer plan I'll leave at retirement</strong>
                    <span className="hint" style={{ display: 'block' }}>
                      The "Rule of 55": if you separate in or after the year you turn 55, this
                      plan alone becomes penalty-free right away — a bridge to 59½ that
                      disappears the moment the money is rolled into an IRA.
                    </span>
                  </span>
                </label>
              )}
              <NumberField label="Invested in stocks, %" value={acct.allocation?.equityPct ?? 60} min={0} max={100}
                tip={TIPS.equityPct}
                hint="How the money inside this account is invested — stocks grow fastest and drop hardest"
                onCommit={(v) => update((p) => { setEquityShare(p.accounts[i]!, v); })} />
              <NumberField label="Invested in bonds/cash, %"
                value={100 - (acct.allocation?.equityPct ?? 60)} min={0} max={100}
                tip={TIPS.bondsCashPct}
                hint="The two always total 100% — fill in one and the other follows. Growth blends the rates from Assumptions."
                onCommit={(v) => update((p) => { setEquityShare(p.accounts[i]!, 100 - v); })} />
              <CurrencyField label="Your annual contribution"
                value={acct.contributions?.employee ?? 0}
                hint={hint ?? 'While working'}
                onCommit={(v) => update((p) => {
                  p.accounts[i]!.contributions = { ...(p.accounts[i]!.contributions ?? { employee: 0 }), employee: v };
                })} />
              {isEmployerPlan(acct.type) && (
                <>
                  <NumberField label="Match: % of salary" min={0} max={100} tip={TIPS.matchPercent}
                    value={acct.contributions?.employerMatch?.percentOfSalary ?? 0}
                    hint="e.g. employer matches on the first 6%"
                    onCommit={(v) => update((p) => {
                      const c = p.accounts[i]!.contributions ?? { employee: 0 };
                      c.employerMatch = { percentOfSalary: v, matchRate: c.employerMatch?.matchRate ?? 0.5, cap: c.employerMatch?.cap ?? 100_000 };
                      p.accounts[i]!.contributions = c;
                    })} />
                  <NumberField label="Match rate %" min={0} max={200} tip={TIPS.matchRate}
                    value={Math.round((acct.contributions?.employerMatch?.matchRate ?? 0.5) * 100)}
                    hint="50 = fifty cents per dollar"
                    onCommit={(v) => update((p) => {
                      const c = p.accounts[i]!.contributions ?? { employee: 0 };
                      c.employerMatch = { percentOfSalary: c.employerMatch?.percentOfSalary ?? 6, matchRate: v / 100, cap: c.employerMatch?.cap ?? 100_000 };
                      p.accounts[i]!.contributions = c;
                    })} />
                </>
              )}
              {acct.inherited && (
                <>
                  <NumberField label="Year of original owner's death" min={2000} max={2026}
                    value={acct.inherited.decedentDeathYear}
                    onCommit={(v) => update((p) => { p.accounts[i]!.inherited!.decedentDeathYear = v; })} />
                  <div className="field">
                    <label>
                      Beneficiary type
                      <select value={acct.inherited.beneficiaryClass}
                        onChange={(e) => update((p) => {
                          p.accounts[i]!.inherited!.beneficiaryClass = e.target.value as 'eligible_designated' | 'non_eligible_designated';
                        })}>
                        <option value="non_eligible_designated">Standard (10-year rule)</option>
                        <option value="eligible_designated">Eligible (spouse, disabled… — stretch)</option>
                      </select>
                    </label>
                  </div>
                  <div className="field">
                    <label>
                      Had the original owner started RMDs?
                      <select value={acct.inherited.decedentHadBegunRMDs ? 'yes' : 'no'}
                        onChange={(e) => update((p) => {
                          p.accounts[i]!.inherited!.decedentHadBegunRMDs = e.target.value === 'yes';
                        })}>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                  </div>
                </>
              )}
            </div>
            <button className="btn danger"
              onClick={() => update((p) => { p.accounts.splice(i, 1); })}>
              Remove account
            </button>
          </details>
        );
      })}

      <div className="add-row">
        <select value={newType} onChange={(e) => setNewType(e.target.value as AccountType)} aria-label="New account type">
          {(Object.keys(ACCOUNT_TYPE_LABELS) as AccountType[]).map((t) => (
            <option key={t} value={t}>{ACCOUNT_TYPE_LABELS[t]}</option>
          ))}
        </select>
        <button className="btn"
          onClick={() => update((p) => {
            const isInherited = newType.startsWith('inherited');
            p.accounts.push({
              id: `acct_${newType}_${p.accounts.length + 1}`,
              ownerId: people[0]!.id,
              type: newType,
              label: ACCOUNT_TYPE_LABELS[newType],
              balance: 0,
              asOfDate: '2026-08-03',
              allocation: DEFAULT_ALLOCATION[newType] ?? { equityPct: 60, bondPct: 35, cashPct: 5 },
              ...(ACCOUNT_META[newType].taxFamily === 'aftertax' ? { costBasis: 0 } : {}),
              ...(isInherited
                ? { inherited: { decedentDeathYear: 2025, beneficiaryClass: 'non_eligible_designated' as const, decedentHadBegunRMDs: true } }
                : {}),
            });
          })}>
          + Add account
        </button>
      </div>
    </div>
  );
}

// ── Income streams ──────────────────────────────────────────────────────────

function IncomeTab({ plan, update }: { plan: Plan; update: (mut: (p: Plan) => void) => void }) {
  const people = plan.household.people;
  const [newType, setNewType] = useState<IncomeType>('w2');

  return (
    <div>
      {plan.incomes.map((inc, i) => {
        const isPension = inc.type.startsWith('pension');
        const isAnnuity = inc.type.startsWith('annuity');
        return (
          <details key={inc.id} className="item-card">
            <summary>
              <strong>{inc.label || INCOME_TYPE_LABELS[inc.type]}</strong>
              <span className="sub">
                {INCOME_TYPE_LABELS[inc.type]} · {people.find((p) => p.id === inc.ownerId)?.label ?? '?'} ·{' '}
                {fmtCompactUsd(inc.annualAmount)}/yr · ages {inc.startAge}–{inc.endAge}
              </span>
            </summary>
            <div className="inputs-grid">
              <div className="field">
                <label>
                  Label
                  <input value={inc.label}
                    onChange={(e) => update((p) => { p.incomes[i]!.label = e.target.value; })} />
                </label>
              </div>
              <div className="field">
                <label>
                  Type
                  <select value={inc.type}
                    onChange={(e) => update((p) => { p.incomes[i]!.type = e.target.value as IncomeType; })}>
                    {(Object.keys(INCOME_TYPE_LABELS) as IncomeType[]).map((t) => (
                      <option key={t} value={t}>{INCOME_TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="field">
                <label>
                  Owner
                  <select value={inc.ownerId}
                    onChange={(e) => update((p) => { p.incomes[i]!.ownerId = e.target.value; })}>
                    {people.map((per) => <option key={per.id} value={per.id}>{per.label}</option>)}
                  </select>
                </label>
              </div>
              <CurrencyField label="Annual amount" value={inc.annualAmount}
                onCommit={(v) => update((p) => { p.incomes[i]!.annualAmount = v; })} />
              <NumberField label="Starts at age" value={inc.startAge} min={18} max={100}
                onCommit={(v) => update((p) => { p.incomes[i]!.startAge = v; })} />
              <NumberField label="Ends at age" value={inc.endAge} min={18} max={120}
                onCommit={(v) => update((p) => { p.incomes[i]!.endAge = v; })} />
              {(isPension || isAnnuity || ['rental', 'royalty', 'other_passive'].includes(inc.type)) && (
                <NumberField label="Annual increase (COLA) %" min={0} max={10}
                  value={Math.round((inc.colaRate ?? 0) * 100)}
                  hint="Most private pensions have none"
                  onCommit={(v) => update((p) => { p.incomes[i]!.colaRate = v / 100; })} />
              )}
              <div className="field">
                <label>
                  Tax treatment
                  <select
                    value={!inc.taxable || inc.taxTreatment === 'tax_free' ? 'tax_free' : inc.taxTreatment ?? 'ordinary'}
                    onChange={(e) => update((p) => {
                      const v = e.target.value as 'ordinary' | 'partially_taxable' | 'tax_free';
                      p.incomes[i]!.taxTreatment = v;
                      p.incomes[i]!.taxable = v !== 'tax_free';
                    })}>
                    <option value="ordinary">Fully taxable</option>
                    <option value="partially_taxable">Partially taxable</option>
                    <option value="tax_free">Tax-free</option>
                  </select>
                </label>
              </div>
              {isAnnuity && (
                <NumberField label="Exclusion ratio %" min={0} max={100}
                  value={Math.round((inc.exclusionRatio ?? 0) * 100)}
                  hint="Portion that is untaxed return of your own money"
                  onCommit={(v) => update((p) => { p.incomes[i]!.exclusionRatio = v / 100; })} />
              )}
              {isPension && people.length === 2 && (
                <>
                  <NumberField label="Survivor % (J&S election)" min={0} max={100}
                    value={inc.survivorElection?.percentToSurvivor ?? 0}
                    hint="0 = single-life pension"
                    onCommit={(v) => update((p) => {
                      const cur = p.incomes[i]!.survivorElection;
                      p.incomes[i]!.survivorElection = v === 0 ? undefined
                        : { percentToSurvivor: v, benefitReduction: cur?.benefitReduction ?? 0.1 };
                    })} />
                  {inc.survivorElection && (
                    <NumberField label="Benefit reduction for J&S %" min={0} max={40}
                      value={Math.round((inc.survivorElection.benefitReduction ?? 0) * 100)}
                      hint="The cost of the survivor option"
                      onCommit={(v) => update((p) => {
                        p.incomes[i]!.survivorElection!.benefitReduction = v / 100;
                      })} />
                  )}
                </>
              )}
            </div>
            <button className="btn danger" onClick={() => update((p) => { p.incomes.splice(i, 1); })}>
              Remove income
            </button>
          </details>
        );
      })}

      <div className="add-row">
        <select value={newType} onChange={(e) => setNewType(e.target.value as IncomeType)} aria-label="New income type">
          {(Object.keys(INCOME_TYPE_LABELS) as IncomeType[]).map((t) => (
            <option key={t} value={t}>{INCOME_TYPE_LABELS[t]}</option>
          ))}
        </select>
        <button className="btn"
          onClick={() => update((p) => {
            const owner = people[0]!;
            const age = 2026 - owner.birthYear;
            p.incomes.push({
              id: `inc_${p.incomes.length + 1}`,
              ownerId: owner.id,
              label: INCOME_TYPE_LABELS[newType],
              type: newType,
              annualAmount: 0,
              startAge: newType === 'w2' || newType === 'self_employment' ? age : 65,
              endAge: newType === 'w2' || newType === 'self_employment' ? owner.retirementAge : 120,
              taxable: true,
              taxTreatment: 'ordinary',
            });
          })}>
          + Add income
        </button>
      </div>
      <p className="hint">
        Multiple streams with different ages are supported — a career change, part-time
        consulting for a few years, a pension starting at 65.
      </p>
    </div>
  );
}

// ── One-time events ─────────────────────────────────────────────────────────

function EventsTab({ plan, update }: { plan: Plan; update: (mut: (p: Plan) => void) => void }) {
  return (
    <div>
      {plan.oneTimeEvents.map((ev, i) => (
        <details key={ev.id} className="item-card">
          <summary>
            <strong>{ev.label || 'Event'}</strong>
            <span className="sub">
              {ev.year} · {ev.amount >= 0 ? '+' : ''}{fmtCompactUsd(ev.amount)}
            </span>
          </summary>
          <div className="inputs-grid">
            <div className="field">
              <label>
                Label
                <input value={ev.label}
                  onChange={(e) => update((p) => { p.oneTimeEvents[i]!.label = e.target.value; })} />
              </label>
            </div>
            <NumberField label="Year" value={ev.year} min={2026} max={2100}
              onCommit={(v) => update((p) => { p.oneTimeEvents[i]!.year = v; })} />
            <CurrencyField label="Amount" value={ev.amount} allowNegative
              hint="Positive = money in (inheritance, sale) · negative = money out (roof, wedding)"
              onCommit={(v) => update((p) => { p.oneTimeEvents[i]!.amount = v; })} />
            <div className="field">
              <label>
                Tax treatment
                <select value={ev.taxTreatment}
                  onChange={(e) => update((p) => {
                    p.oneTimeEvents[i]!.taxTreatment = e.target.value as OneTimeEvent['taxTreatment'];
                  })}>
                  <option value="tax_free">Tax-free (inheritance, gift)</option>
                  <option value="ordinary">Ordinary income</option>
                  <option value="ltcg">Long-term capital gain (business or property sale)</option>
                </select>
              </label>
              {ev.taxTreatment === 'ltcg' && (
                <span className="hint">
                  Taxed at 0/15/20% stacked on top of your other income, plus the 3.8% surtax
                  where it applies. Assumes you held it more than a year.
                </span>
              )}
            </div>
            {ev.taxTreatment === 'ltcg' && ev.amount > 0 && (
              <CurrencyField label="What you paid for it (cost basis)" value={ev.costBasis ?? 0}
                tip={TIPS.eventCostBasis}
                hint="Leave at 0 to tax the whole amount as gain — typical for a business you built."
                onCommit={(v) => update((p) => { p.oneTimeEvents[i]!.costBasis = v; })} />
            )}
            {ev.amount >= 0 && (
              <div className="field">
                <label>
                  {ev.destinations?.length ? 'Remainder deposited into' : 'Deposit into'}
                  <select value={ev.destinationAccountId ?? ''}
                    onChange={(e) => update((p) => {
                      p.oneTimeEvents[i]!.destinationAccountId = e.target.value || undefined;
                    })}>
                    <option value="">Spend / general cash</option>
                    {plan.accounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
          {ev.amount >= 0 && (
            <div style={{ padding: '0 var(--space-4) var(--space-3)' }}>
              <strong style={{ fontSize: 'var(--fs-micro)', color: 'var(--ink-secondary)' }}>
                Split the deposit
              </strong>
              <p className="hint" style={{ margin: '2px 0 8px' }}>
                Send fixed amounts to specific accounts first — top up the safety bucket, say —
                and the rest goes to the account chosen above (or is spent). Splits apply in
                order and never exceed the event amount.
              </p>
              {(ev.destinations ?? []).map((split, si) => (
                <div key={si} className="add-row" style={{ marginBottom: 6 }}>
                  <select
                    value={split.accountId}
                    aria-label="Split destination account"
                    onChange={(e) => update((p) => {
                      p.oneTimeEvents[i]!.destinations![si]!.accountId = e.target.value;
                    })}
                  >
                    {plan.accounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.label}</option>
                    ))}
                  </select>
                  <CurrencyField label="" value={split.amount}
                    onCommit={(v) => update((p) => {
                      p.oneTimeEvents[i]!.destinations![si]!.amount = v;
                    })} />
                  <button className="btn danger"
                    onClick={() => update((p) => {
                      p.oneTimeEvents[i]!.destinations!.splice(si, 1);
                      if (p.oneTimeEvents[i]!.destinations!.length === 0) {
                        p.oneTimeEvents[i]!.destinations = undefined;
                      }
                    })}>
                    Remove
                  </button>
                </div>
              ))}
              {plan.accounts.length > 0 && (
                <button className="btn"
                  onClick={() => update((p) => {
                    p.oneTimeEvents[i]!.destinations ??= [];
                    p.oneTimeEvents[i]!.destinations!.push({
                      accountId: plan.accounts[0]!.id,
                      amount: 0,
                    });
                  })}>
                  + Add split
                </button>
              )}
            </div>
          )}
          {ev.taxTreatment === 'ltcg' && ev.amount > 0 && (
            <div style={{ padding: '0 var(--space-4) var(--space-3)' }}>
              <SaleImpactNote plan={plan} eventId={ev.id} />
            </div>
          )}
          <button className="btn danger" onClick={() => update((p) => { p.oneTimeEvents.splice(i, 1); })}>
            Remove event
          </button>
        </details>
      ))}

      <div className="add-row">
        <button className="btn"
          onClick={() => update((p) => {
            p.oneTimeEvents.push({
              id: `ev_${p.oneTimeEvents.length + 1}`,
              year: 2030,
              label: 'One-time event',
              amount: 0,
              taxTreatment: 'tax_free',
            });
          })}>
          + Add one-time event
        </button>
      </div>
      <p className="hint">
        Inheritance, a business sale, a wedding, a new roof — anything that happens once.
      </p>
    </div>
  );
}
